import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import { SurveyFieldDto } from 'apps/marsinet/src/app/typescript-axios';

interface QuestionsTableProps {
  template: SurveyFieldDto[];
  onTemplateChange: (template: SurveyFieldDto[]) => void;
}

const QuestionsTable: React.FC<QuestionsTableProps> = ({
  template,
  onTemplateChange,
}) => {
  const { t } = useTranslation();
  const toastRef = useRef<Toast>(null);

  const getFieldsDependingOn = (fieldName: string) =>
    template.filter((field) => field.condition?.field_name === fieldName);

  const handleRemoveQuestion = (rowIndex: number) => {
    const fieldToRemove = template[rowIndex];
    const dependentFields = getFieldsDependingOn(fieldToRemove.name);

    if (dependentFields.length > 0) {
      toastRef.current?.show({
        severity: 'warn',
        summary: t('pages.surveys.warning'),
        detail: t('pages.surveys.cannot_delete_field_has_dependencies'),
        life: 3000,
      });
      return;
    }

    onTemplateChange(template.filter((_, i) => i !== rowIndex));
  };

  if (template.length === 0) return null;

  return (
    <>
      <Toast ref={toastRef} />
      <div className="mb-3 mt-3">
        <DataTable
          value={template}
          size="small"
          responsiveLayout="scroll"
          className="p-datatable-sm"
        >
          <Column
            field="name"
            header={t('pages.surveys.question_title')}
            style={{ width: '12%' }}
          />
          <Column
            field="description"
            header={t('pages.surveys.question_description')}
            style={{ width: '15%' }}
          />
          <Column
            field="required"
            header={t('pages.surveys.required')}
            style={{ width: '8%' }}
            body={(rowData) =>
              rowData.required ? (
                <i className="pi pi-check text-green-500" />
              ) : (
                <i className="pi pi-times text-red-500" />
              )
            }
          />
          <Column
            field="type"
            header={t('pages.surveys.question_type')}
            style={{ width: '10%' }}
            body={(rowData: any) => t(`pages.surveys.field_types.${rowData.type}`)}
          />
          <Column
            header={t('pages.surveys.question_options')}
            style={{ width: '15%' }}
            body={(rowData: any) =>
              rowData.allowed_values && rowData.allowed_values.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {rowData.allowed_values.map((value: string, idx: number) => (
                    <span
                      key={idx}
                      className="px-2 py-1 border-1 border-round surface-border text-sm"
                    >
                      {value}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-color-secondary">-</span>
              )
            }
          />
          <Column
            header={t('pages.surveys.depends_on_field')}
            style={{ width: '32%' }}
            body={(rowData: any) =>
              rowData.condition ? (
                <div>
                  <small className="text-color-secondary">
                    {rowData.condition.field_name} = {rowData.condition.value}
                  </small>
                </div>
              ) : (
                <span className="text-color-secondary">-</span>
              )
            }
          />
          <Column
            header={t('pages.devices.table.actions')}
            style={{ width: '8%' }}
            body={(rowData, { rowIndex }) => (
              <Button
                type="button"
                icon="pi pi-trash"
                className="p-button-rounded p-button-danger p-button-text p-button-sm"
                onClick={() => handleRemoveQuestion(rowIndex)}
              />
            )}
          />
        </DataTable>
      </div>
    </>
  );
};

export default QuestionsTable;